import { safeParseLessonContent, type LessonContent } from "@/lib/lesson-content/schema";
import { GPU_LESSON_FIXTURE } from "@/lib/ile/fixtures/gpu-lesson";
import { isInteractiveBlock } from "@/lib/ile/block-meta";
import type { BlockType } from "@/lib/lesson-content/schema";

const GPU_LEGACY_BLOCK_IDS = new Set(
  GPU_LESSON_FIXTURE.sections.flatMap((section) => section.blocks.map((block) => block.id)),
);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Derive a stable section UUID from a block UUID (legacy flat → one block per section). */
function sectionIdFromBlockId(blockId: string, index: number): string {
  const uuidMatch = /^([0-9a-f]{8})-([0-9a-f]{4})-4([0-9a-f]{3})-([89ab][0-9a-f]{3})-([0-9a-f]{12})$/i.exec(
    blockId,
  );
  if (uuidMatch) {
    const segment = String(index + 1).padStart(4, "0");
    return `${uuidMatch[1]}-${segment}-4${uuidMatch[3]}-${uuidMatch[4]}-${uuidMatch[5]}`;
  }
  return `00000000-0000-4000-8000-${String(index + 1).padStart(12, "0")}`;
}

function stripBlockForSection(block: Record<string, unknown>): Record<string, unknown> {
  const { required_to_advance: _removed, ...rest } = block;
  return rest;
}

/**
 * Converts pre-sections `{ blocks: [...] }` payloads into `{ sections: [...] }`.
 * Returns null when the input is not a legacy flat blocks lesson.
 */
export function migrateLegacyFlatBlocks(value: unknown): unknown | null {
  if (!isRecord(value)) return null;
  if (value.version !== 1 || value.format !== "blocks") return null;
  if (Array.isArray(value.sections)) return null;
  if (!Array.isArray(value.blocks) || value.blocks.length === 0) return null;

  const blocks = value.blocks.filter(isRecord);
  if (blocks.length === 0) return null;

  const blockIds = blocks.map((block) => String(block.id ?? ""));
  const isGpuDemo =
    blockIds.length === GPU_LEGACY_BLOCK_IDS.size &&
    blockIds.every((id) => GPU_LEGACY_BLOCK_IDS.has(id));

  if (isGpuDemo) {
    return GPU_LESSON_FIXTURE;
  }

  return {
    version: 1,
    format: "blocks",
    sections: blocks.map((block, index) => {
      const type = block.type as BlockType | undefined;
      const requiredFromBlock =
        typeof block.required_to_advance === "boolean" ? block.required_to_advance : undefined;
      const required_to_advance =
        requiredFromBlock ?? (type ? isInteractiveBlock(type) : true);

      return {
        id: sectionIdFromBlockId(String(block.id ?? index), index),
        required_to_advance,
        blocks: [stripBlockForSection(block)],
      };
    }),
  };
}

export function parseLessonContentWithLegacy(value: unknown): LessonContent | null {
  const direct = safeParseLessonContent(value);
  if (direct.success) {
    return direct.data;
  }

  const migrated = migrateLegacyFlatBlocks(value);
  if (!migrated) {
    return null;
  }

  const reparsed = safeParseLessonContent(migrated);
  return reparsed.success ? reparsed.data : null;
}
